import React, { useState, useEffect } from "react";
import { Flex, Table, message, Tooltip, Tag } from "antd";
import CommonDrawer from "../../components/CommonDrawer";
import CustomDeleteModal from "../Modals/CustomDeleteModal";
import { CustomParagraph, CustomTitle, AddItemButton } from "../../components";
import { useToken } from "../../common/helper";
import { useNavigate } from "react-router-dom";
import { useSpin } from "../../components/SpinContext.js";
import editIcon from "../../assets/images/edit-icon.svg";
import deleteIcon from "../../assets/images/delete.svg";
import NotificationForm from "./NotificationForm";
import { ProductTags } from "./ProductTags";
import {
  DeleteNotification,
  GetNotificationModel,
  GetProducts,
} from "../../api's/onboarding";
import { GetAllPendingUsers } from "../../api's/settings.js";
import { useSelector } from "react-redux";
import { getPermissions } from "../../common/helper/getPermissions.js";
import TeamsTag from "../../components/TeamsTag.jsx";
import CustomTextWithTooltip from "../../components/CustomTextWithTooltip.jsx";

const NotificationList = ({ title }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [step, setStep] = useState(1);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [productList, setProductList] = useState();
  const [selectedType, setSelectedType] = useState();
  const { token, removeToken, decodedUserToken, getUserID } = useToken();
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const [notificationList, setNotificationList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const main_menu = useSelector((state) => state.menu.items);
  const notificationPermissions = getPermissions(
    main_menu,
    "Settings",
    "Notifications"
  );
  let userName = decodedUserToken()?.name;
  let userEmail = decodedUserToken()?.email;
  const getUsers = () => {
    showSpin();
    GetAllPendingUsers(token, true)
      .then((res) => {
        if (res?.data?.data?.is_success) {
          const users = res.data.data?.data || [];
          setUsersList(users);
        }
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        message.error("Error fetching users");
      })
      .finally(() => hideSpin());
  };
  const mapAlertsToTableData = (alerts) => {
    const getFrequencyLabel = (frequency, alert) => {
      if (!frequency) return "—";

      switch (frequency) {
        case "ONCE_A_DAY":
          return `Once a day at ${alert.time_1 || "—"}`;
        case "TWICE_A_DAY":
          return `Twice a day at ${alert.time_1 || "—"} & ${
            alert.time_2 || "—"
          }`;
        case "WEEKLY":
          return `Weekly on ${alert.day || "—"} at ${alert.time_2 || "—"}`;
        case "REALTIME":
          return "Real Time";
        case "BATCHEDALERTS":
          return "Batched Alerts";
        case "15MIN":
          return "Every 15 Mins";
        case "30MIN":
          return "Every 30 Mins";
        case "1HR":
          return "Every 1 Hour";
        default:
          return "—";
      }
    };

    const formatDate = (iso) => {
      if (!iso) return "—";
      const options = {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      };
      return new Date(iso).toLocaleString("en-US", options);
    };

    return alerts.map((alert) => {
      let alertTypeLabel = "";

      const isCritical = alert.is_critical;
      const isAtRisk = alert.is_at_risk;
      const isPickup = alert.is_pickup_confirmation;
      const isDelivery = alert.is_delivery_confirmation;

      if (isCritical && isAtRisk) {
        alertTypeLabel = "Critical & At-risk Alert";
      } else if (isCritical) {
        alertTypeLabel = "Critical Alert";
      } else if (isAtRisk) {
        alertTypeLabel = "At-risk Alert";
      }

      if (isPickup && isDelivery) {
        alertTypeLabel = "Pickup / Delivery Confirmation";
      } else if (isPickup) {
        alertTypeLabel = "Pickup Confirmation";
      } else if (isDelivery) {
        alertTypeLabel = "Delivery Confirmation";
      } else if (
        alert.alert_type === "AT_LOCATION" ||
        alert.alert_type === "at_location"
      ) {
        alertTypeLabel = "At Location";
      } else if (
        alert.alert_type === "ETA_BASED" ||
        alert.alert_type === "eta_based"
      ) {
        alertTypeLabel = "ETA";
      }

      return {
        key: alert.id,
        type: alertTypeLabel,
        products: alert.products.map((p) => p.name),
        frequency: getFrequencyLabel(alert.frequency, alert),
        sendTo: alert?.send_to || [],
        createdBy: alert.created_by_name || "—",
        lastUpdated: formatDate(alert.updated_at),
        originalData: alert,
      };
    });
  };
  const tableData = mapAlertsToTableData(notificationList);
  const unauthorize = (error) => {
    if (error && error?.status === 401) {
      removeToken();
      navigate("/login");
    }
  };

  const fetchNotifications = () => {
    showSpin();
    GetNotificationModel(token)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let res = data?.data?.data?.length > 0 ? data?.data?.data : [];
            setNotificationList(res);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const fetchProductLists = () => {
    showSpin();
    GetProducts(token)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let res = data.data.data;
            if (res) {
              setProductList(res);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const handleEdit = (record) => {
    setIsEditing(true);
    setCurrentNotification(record.originalData);
    setDrawerOpen(true);
  };

  const handleDeleteClick = (notification) => {
    setSelectedNotification(notification);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    showSpin();
    DeleteNotification(selectedNotification?.originalData?.id, token)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            message.success(data?.data?.message);
            fetchNotifications();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });

    setDeleteModalVisible(false);
    setSelectedNotification(null);
  };

  const handleAddNotification = () => {
    setIsEditing(false);
    setCurrentNotification(null);
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "12%",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <CustomParagraph
            text={text || "—"}
            fontSize={14}
            fontWeight={600}
            color="#000"
          />
        </div>
      ),
    },
    {
      title: "Products",
      dataIndex: "products",
      key: "products",
     width: "18%",
      render: (products) => (
        <ProductTags
          products={products && products.length > 0 ? products : ["—"]}
          padding="3px 8px"
        />
      ),
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      width: "11%",
      
      render: (text) => (
        // <CustomParagraph
        //   text={text || "—"}
        //   fontSize={14}
        //   fontWeight={400}
        //   color="#333"
        // />
        <div
        style={{
          width: "100px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          height: "19px",
          lineHeight: "19px",
        }}
      >
        <CustomTextWithTooltip
          text={text || "—"}
          fontSize={14}
          fontWeight={400}
          color="#333"
          className="productTagsStyle"
        />
        </div>
      ),
    },
    {
      title: "Send to",
      dataIndex: "sendTo",
     width: "20%",
      key: "send_to",
      render: (sendTo, record) => {
        if (
          (!Array.isArray(sendTo) || sendTo.length === 0) &&
          Array.isArray(record?.originalData?.users)
        ) {
          const userNames = record.originalData.users
            .map((u) => u.name || u.email)
            .filter(Boolean);

          if (userNames.length > 0) {
            return (
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {userNames.map((name, index) => (
                  <span key={index} style={{ color: "#333", fontSize: 12 }}>
                    {name}
                  </span>
                ))}
              </div>
            );
          }
        }
        if (
          (!Array.isArray(sendTo) || sendTo.length === 0) &&
          userName
        ) {
          const id = getUserID();


          if (id) {
            return (
              <span style={{ color: "#333", fontSize: 12 }}>
                {userName || userEmail || "—"}
              </span>
            );
          }
        }

        // if (!Array.isArray(sendTo) || sendTo.length === 0) {
        //   return (
        //     <CustomParagraph
        //       text="—"
        //       fontSize={14}
        //       fontWeight={400}
        //       color="#333"
        //     />
        //   );
        // }

        const teamsWithAll = sendTo.filter((team) => team.select_all);
        const teamsWithUsers = sendTo.filter((team) => !team.select_all);
        const allUserNames = teamsWithUsers
          .flatMap((team) => team.users || [])
          .map((user) => user.name || user.email)
          .filter(Boolean);

        const visibleItems = [];
        const overflowItems = [];

        if (teamsWithAll.length > 0) {
          visibleItems.push({ type: "tag", team: teamsWithAll[0] });
          for (let i = 1; i < teamsWithAll.length; i++) {
            overflowItems.push(teamsWithAll[i].name);
          }
        }

        const nameLimit = visibleItems.length === 1 ? 1 : 2;
        const visibleNames = allUserNames.slice(0, nameLimit);
        const overflowNames = allUserNames.slice(nameLimit);

        visibleNames.forEach((name) =>
          visibleItems.push({ type: "name", name })
        );
        overflowItems.push(...overflowNames);

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "6px",
            }}
          >
            {visibleItems.map((item, idx) => {
              if (item.type === "tag") {
                const teamData = {
                  [item.team.id]: {
                    name: item.team.name,
                    color: item.team.color || "#000",
                    role_name: "All Members",
                  },
                };
                return (
                  <TeamsTag
                    key={`team-${item.team.id}-${idx}`}
                    padding={false}
                    teamData={teamData}
                    border={false}
                  />
                );
              } else {
                return (
                  <span
                    key={`user-${item.name}-${idx}`}
                    style={{ color: "#333", fontSize: 12 }}
                  >
                    {item.name}
                  </span>
                );
              }
            })}

            {overflowItems.length > 0 && (
              <Tooltip title={overflowItems.join(", ")}>
                <span
                  style={{ cursor: "pointer", color: "#333", fontSize: 12 }}
                >
                  +{overflowItems.length} more
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },

    {
      title: "Created by",
      dataIndex: "createdBy",
      key: "created_by_name",
      width: "15%",
      render: (text) => (
        <CustomParagraph
          text={text || "—"}
          fontSize={14}
          fontWeight={400}
          color="#333"
        />
      //   <CustomTextWithTooltip
      //   text={text || "—"}
      //   fontSize={14}
      //   fontWeight={400}
      //   color="#333"
      //   className="productTagsStyle"
      // />
      ),
    },
    {
      title: "Last updated",
      dataIndex: "lastUpdated",
      key: "updated_at",
      width: "18%",
      render: (text) => (
        <CustomParagraph
          text={text || "—"}
          fontSize={14}
          fontWeight={400}
          color="#333"
        />
      ),
    },
    {
      title: "",
     // width: "10%",
      key: "action",
      render: (_, record) => {
        return (
          <Flex>
            {record?.originalData?.permissions?.is_update && (
              <img
                src={editIcon}
                alt="Edit"
                onClick={() => handleEdit(record)}
                style={{
                  cursor: "pointer",
                  marginRight: "10px",
                  padding: "4px",
                  borderRadius: "50%",
                  transition: "background 0.2s ease-in-out",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#00000014")}
                onMouseLeave={(e) =>
                  (e.target.style.background = "transparent")
                }
              />
            )}

            {record?.originalData?.permissions?.is_delete && (
              <img
                src={deleteIcon}
                alt="Delete"
                onClick={() => handleDeleteClick(record)}
                style={{
                  cursor: "pointer",
                  //marginRight: "10px",
                  padding: "4px",
                  borderRadius: "50%",
                  transition: "background 0.2s ease-in-out",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#00000014")}
                onMouseLeave={(e) =>
                  (e.target.style.background = "transparent")
                }
              />
            )}
          </Flex>
        );
      },
    },
  ];
  useEffect(() => {
    getUsers();
    fetchProductLists();
    fetchNotifications();
  }, []);

  return (
    <>
      <div className="settings-content-wrapper">
        <CustomTitle
          text={`${title} (${notificationList.length})`}
          fontSize={20}
          fontWeight="700"
          color="#000"
          lineHeight="32px"
          marginBottom={0}
        />

        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          className="settings-table transport-provider-table notificatiolist-table"
        />
        {notificationPermissions?.is_create && (
          <AddItemButton
            marginTop="0px"
            fontSize="12px"
            fontWeight="600"
            text={"Add notification"}
            onClick={handleAddNotification}
          />
        )}
      </div>

      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Notification"
        message="By removing it, you will no longer receive this notification. Other users will not be affected."
        cancelText="Cancel"
        confirmText="Remove"
        confirmColor="#DE1135"
      />
      {drawerOpen && (
        <CommonDrawer
          onBack={
            step > 1 && !isEditing
              ? () => setStep((prev) => prev - 1)
              : undefined
          }
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
        >
          <NotificationForm
            notificationPermissions={notificationPermissions}
            users={usersList}
            setUsersList={setUsersList}
            unauthorize={unauthorize}
            fetchNotifications={fetchNotifications}
            step={step}
            setStep={setStep}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            productList={productList}
            isEditing={isEditing}
            drawerOpen={drawerOpen}
            notificationData={currentNotification}
            onClose={() => setDrawerOpen(false)}
          />
        </CommonDrawer>
      )}
    </>
  );
};

export default NotificationList;
